/**
 * Where a dealership keeps its stock, and therefore whether demurrage applies.
 *
 * Demurrage is what a bonded customs warehouse charges per day to store a
 * vehicle. The app used to assume every dealership pays it — DailyDemurrage was
 * `required` on every Imported unit — so a dealership that keeps its imports in
 * its OWN YARD was made to type a number for a bill it never receives. There is
 * no honest value to enter there: 0 asserts a charge of zero where in fact
 * there is no charge, and anything else is invented.
 *
 * The question has to be asked twice, because it has two different answers:
 *
 *   the DEALERSHIP  which kinds of storage do you use?   Dealerships.StorageSettings
 *   the VEHICLE     which one is THIS car in?            Inventory/Sales.StorageType
 *
 * A dealership that uses both can have one car in its yard and the next in a
 * bond, so a single per-tenant switch cannot answer it.
 *
 * MIRRORED, BY HAND, in node/helpers/storage.js. It has to be: node deploys
 * without reactor's source, so it cannot import this file, and the rule has to
 * hold on a request that never went near a browser. The two copies are pinned
 * against ONE shared table of cases in storage.cases.json — see the test at the
 * bottom of both suites. That table is the actual specification; if you change
 * behaviour here, the case table is what forces the other copy to follow.
 */

export const BOND = "Bond";
export const YARD = "Yard";

/**
 * The kinds of storage this dealership uses.
 *
 * An empty or unreadable setting means UNANSWERED, and unanswered has to mean
 * bond-only — that is exactly how the app behaved before any of this existed,
 * and every tenant is unanswered until somebody opens Settings. Returning []
 * here instead would quietly stop asking for demurrage from every dealership
 * that pays it, which is a far worse failure than asking one that does not.
 */
export function storageTypes(settings) {
    const raw = settings && Array.isArray(settings.types) ? settings.types : [];
    const kept = raw.filter((t) => t === BOND || t === YARD);
    return kept.length ? [...new Set(kept)] : [BOND];
}

/** Does this dealership use both kinds, and so have to ask per vehicle? */
export function usesBothStorageTypes(settings) {
    return storageTypes(settings).length > 1;
}

/** The named locations for one kind, in settings order. */
export function storageLocations(settings, type) {
    const all = settings && Array.isArray(settings.locations) ? settings.locations : [];
    const usable = storageTypes(settings);
    return all.filter((l) => l && typeof l.name === "string" && l.name.trim()
        && (type ? l.type === type : usable.includes(l.type)));
}

/**
 * What a NEW vehicle should be assumed to be in, and what a legacy row with no
 * StorageType resolves to.
 *
 * A location explicitly marked default wins, because that is the dealership
 * saying so in as many words. Otherwise Bond when it is available — same
 * reasoning as storageTypes: the conservative answer is the one that keeps
 * asking for a rate that might be owed.
 */
export function defaultStorageType(settings) {
    const types = storageTypes(settings);
    const marked = storageLocations(settings).find((l) => l.isDefault);
    if (marked && types.includes(marked.type)) return marked.type;
    return types.includes(BOND) ? BOND : YARD;
}

/** The location name to prefill on a new vehicle of the given kind. */
export function defaultLocationName(settings, type) {
    const wanted = type || defaultStorageType(settings);
    const forType = storageLocations(settings, wanted);
    return (forType.find((l) => l.isDefault) || forType[0] || {}).name || "";
}

/**
 * Which kind of storage a particular vehicle is in.
 *
 * An explicit value on the ROW always wins, even if the dealership no longer
 * lists that kind. The row is recording a fact about where a car actually sat;
 * a later settings change does not move the car, and silently re-reading it as
 * a bond would start billing demurrage on a vehicle somebody deliberately
 * marked as sitting in the yard.
 *
 * Only a missing value falls through to the dealership default — which is the
 * legacy case: every row predating this feature has StorageType NULL, and they
 * must keep behaving exactly as they did.
 */
export function resolveStorageType(record, settings) {
    const raw = record && record.StorageType;
    const value = typeof raw === "string" ? raw.trim() : raw;
    if (value === BOND || value === YARD) return value;
    return defaultStorageType(settings);
}

/**
 * Does demurrage apply to this vehicle at all?
 *
 * The one question every screen asks. Keeping it in a single function is what
 * stops the modal, the sell wizard, the importer and the server from each
 * deciding it slightly differently — the failure mode costExpr.js exists to
 * document, where one definition of TotalCost became two.
 */
export function chargesDemurrage(record, settings) {
    return resolveStorageType(record, settings) === BOND;
}
